<script lang="ts">
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { getRequest } from "@/Util";

    /**
     * Details a provider could not give us, collected before the account exists.
     *
     * Strava returns null for both `email` and `username` on many athletes, so
     * without this the signup dead-ended. The username is asked for even when the
     * provider did supply an email, because it is public on community and coach
     * pages and used to be auto-generated with no say from the user.
     */
    export let provider: string
    export let providerLabel: string
    export let email: string | null = null
    /** A verified provider email is fixed: editing it would forfeit the guarantee. */
    export let emailLocked: boolean = false
    export let username: string = ''
    export let firstName: string = ''
    export let lastName: string = ''

    let emailValue = email ?? ''
    let usernameValue = username
    let firstNameValue = firstName
    let lastNameValue = lastName

    let loading = false
    let serverError = ''
    let fieldErrors: Record<string, string> = {}

    async function submit() {
        fieldErrors = {}
        serverError = ''
        loading = true

        try {
            const res = await fetch('/auth/social/complete', {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify({
                    email: emailValue,
                    username: usernameValue,
                    first_name: firstNameValue,
                    last_name: lastNameValue,
                }),
            })
            const data = await res.json().catch(() => ({}))

            if (res.ok && data.redirect) {
                window.location.href = data.redirect
                return
            }

            if (data?.errors) {
                const next: Record<string, string> = {}
                for (const [k, msgs] of Object.entries(data.errors as Record<string, string[]>)) {
                    next[k] = (msgs as string[])[0]
                }
                fieldErrors = next
            } else {
                // 419 carries the expired-session message from the server.
                serverError = data?.message ?? 'We could not create your account. Please try again.'
            }
        } catch {
            serverError = 'A network error occurred. Please try again.'
        } finally {
            loading = false
        }
    }
</script>

<div class="space-y-5">
    <div class="space-y-2 text-center">
        <h1 class="text-2xl font-semibold tracking-tight">Finish creating your account</h1>
        <p class="text-sm text-muted-foreground">
            You're signed in with {providerLabel}. We just need a few details to set up your Xert account.
        </p>
    </div>

    <form on:submit|preventDefault={submit} class="space-y-4">
        {#if serverError}
            <div class="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {serverError}
            </div>
        {/if}

        <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
                <Label for="social-first">First name</Label>
                <Input id="social-first" type="text" autocomplete="given-name" bind:value={firstNameValue} disabled={loading} required />
                {#if fieldErrors.first_name}<p class="text-xs text-destructive">{fieldErrors.first_name}</p>{/if}
            </div>
            <div class="space-y-1.5">
                <Label for="social-last">Last name</Label>
                <Input id="social-last" type="text" autocomplete="family-name" bind:value={lastNameValue} disabled={loading} required />
                {#if fieldErrors.last_name}<p class="text-xs text-destructive">{fieldErrors.last_name}</p>{/if}
            </div>
        </div>

        <div class="space-y-1.5">
            <Label for="social-email">Email</Label>
            <Input
                id="social-email"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                bind:value={emailValue}
                disabled={loading || emailLocked}
                readonly={emailLocked}
                required
            />
            {#if emailLocked}
                <p class="text-xs text-muted-foreground">Confirmed by {providerLabel}.</p>
            {:else if provider === 'strava'}
                <p class="text-xs text-muted-foreground">Strava doesn't share your email address, so we need it here.</p>
            {/if}
            {#if fieldErrors.email}<p class="text-xs text-destructive">{fieldErrors.email}</p>{/if}
        </div>

        <div class="space-y-1.5">
            <Label for="social-username">Username</Label>
            <Input id="social-username" type="text" autocomplete="username" bind:value={usernameValue} disabled={loading} required />
            <p class="text-xs text-muted-foreground">This is how you'll appear to other athletes.</p>
            {#if fieldErrors.username}<p class="text-xs text-destructive">{fieldErrors.username}</p>{/if}
        </div>

        <Button type="submit" class="w-full" disabled={loading}>
            {#if loading}
                <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {/if}
            Create account
        </Button>
    </form>

    <p class="text-center text-sm text-muted-foreground">
        <a href="/auth" class="font-medium text-xerteal-600 hover:underline">Cancel and go back</a>
    </p>
</div>
